import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Button from '../../buttons/Button/Button';

/**
 * A component for displaying form progression
 */
function Progress({ header, steps = [], completion, footer, isDynamic }) {
  const { t } = useTranslation();

  return (
    <>
      {header && (
        <p className="form__progress-header" aria-hidden="true">
          {header}
        </p>
      )}
      <div
        className={classNames('form__progress', { ['form__progress--has-dynamic-progress-bars']: isDynamic })}
        aria-label="Progress indicator"
        data-step-count={steps.length}
      >
        {steps.map((step, index) => (
          <Step {...step} key={index} isDynamic={isDynamic} />
        ))}
        {completion && (
          <span className="visually-hidden">{`${completion}% ${t('styleguide:forms.progress.complete')}`}</span>
        )}
      </div>
      {footer && (
        <p className="form__progress-header" aria-hidden="true">
          {footer}
        </p>
      )}
    </>
  );
}

function Step({ title, completion = 0, isCurrent, isComplete, isDisabled, isDynamic, onPress }) {
  const { t } = useTranslation();

  const completionPercentage = isComplete ? 100 : completion;

  return (
    <div
      className={classNames('progress__step', { ['step--complete']: isComplete })}
      aria-label={
        `${t('styleguide:forms.progress.step')} '${title}'` +
        (isComplete ? `(${t('styleguide:forms.progress.complete')})` : '')
      }
      aria-current={isCurrent && 'step'}
      aria-hidden={isDisabled}
    >
      {isDynamic && (
        <div className="progress__bar" role="presentation">
          <div
            className="bar"
            style={{
              clipPath: `polygon(0px 0px, ${completionPercentage}% 0%, ${completionPercentage}% 100%, 0% 100%)`
            }}
          />
        </div>
      )}
      <Button
        className="step__button"
        aria-label={
          `${t('styleguide:forms.progress.goToStep')}: ${title}` +
          (isComplete ? `(${t('styleguide:forms.progress.complete')})` : '')
        }
        asLink={false}
        isDisabled={isDisabled}
        onPress={onPress}
      >
        {title}
      </Button>
    </div>
  );
}

Progress.propTypes = {
  /**
   * The header
   */
  header: PropTypes.string,
  /**
   * The steps
   */
  steps: PropTypes.arrayOf(PropTypes.object),
  /**
   * The completion value
   */
  completion: PropTypes.number,
  /**
   * The footer
   */
  footer: PropTypes.string,
  /**
   * The dynamic flag
   */
  isDynamic: PropTypes.bool
};

export default Progress;
